/* Progressive dimensional selection for the tire PDP; native variant-selection owns the
   form, gallery and availability. Unknown markings keep the original selector visible. */
(function () {
  const fields = ['width', 'profile', 'diameter'];
  const parse = title => {
    const match = /^(LT|P)?(\d{3})\s*\/\s*(\d{2})\s*(Z?R)\s*(\d{2})\s+(\d{2,3}(?:\/\d{2,3})?[LMNPQRSTUHVWY](?:\s+(?:SL|XL|[C-F]))?)$/.exec(String(title || '').trim());
    if (!match) return null;
    const [, type = '', width, profile, construction, diameter, specification] = match;
    return { type, width, profile, construction, diameter, specification };
  };
  const geometryKey = size => fields.map(field => size[field]).join('/');
  const optionsFor = (variants, state, field) => {
    const prior = fields.slice(0, fields.indexOf(field));
    if (prior.some(key => !state[key])) return [];
    const matches = variants.filter(v => prior.every(key => v.size[key] === state[key]));
    return [...new Set(matches.map(v => v.size[field]))].sort((a, b) => Number(a) - Number(b))
      .map(value => ({ value, available: matches.some(v => v.size[field] === value && v.available) }));
  };
  const reconcile = (variants, requested, autoFill = true) => {
    const state = { ...requested };
    fields.forEach(field => {
      const options = optionsFor(variants, state, field);
      if (!options.some(option => option.value === state[field])) state[field] = '';
      if (!state[field] && autoFill && options.length === 1) state[field] = options[0].value;
    });
    const matches = fields.every(field => state[field]) ? variants.filter(v => geometryKey(v.size) === geometryKey(state)) : [];
    state.id = matches.some(v => String(v.id) === String(state.id)) ? String(state.id) : '';
    if (matches.length === 1) state.id = String(matches[0].id);
    return { state, matches, variant: matches.find(v => String(v.id) === state.id) || null };
  };
  const differentSpec = (other, selected) => {
    const changes = [];
    if (other.type !== selected.type) changes.push('Type: ' + (other.type || 'standard') + ' (current: ' + (selected.type || 'standard') + ')');
    if (other.construction !== selected.construction) changes.push('Construction: ' + other.construction + ' (current: ' + selected.construction + ')');
    if (other.specification !== selected.specification) changes.push('Rating: ' + other.specification + ' (current: ' + selected.specification + ')');
    return changes.join(' · ');
  };
  const alternativesFor = (payload, currentProduct, selected) => {
    if (!payload || payload.complete !== true || !Array.isArray(payload.products) || !selected) return [];
    const results = [];
    payload.products.filter(Boolean).forEach(product => {
      if (String(product.id) === String(currentProduct) || !Array.isArray(product.variants)) return;
      const matches = product.variants.filter(Boolean).map(v => ({ ...v, size: parse(v.title) }))
        .filter(v => v.available && v.size && geometryKey(v.size) === geometryKey(selected.size));
      matches.sort((a, b) => Number(!!differentSpec(a.size, selected.size)) - Number(!!differentSpec(b.size, selected.size)) || String(a.title).localeCompare(String(b.title)));
      const variant = matches[0];
      if (variant && /^\/products\/[a-z0-9-]+$/.test(product.url) && /^\d+$/.test(String(variant.id))) {
        results.push({ ...variant, model: product.title, url: product.url + '?variant=' + variant.id, difference: differentSpec(variant.size, selected.size) });
      }
    });
    return results.sort((a, b) => Number(!!a.difference) - Number(!!b.difference) || a.model.localeCompare(b.model)).slice(0, 4);
  };

  const init = async (win, doc) => {
    const dataNode = doc.querySelector('[data-aw-tire-variants]');
    if (!dataNode) return;
    let data;
    try { data = JSON.parse(dataNode.textContent); } catch (_) { return; }
    if (!Array.isArray(data.variants)) return;
    const variants = data.variants.map(v => ({ ...v, size: parse(v.title) }));
    if (!variants.length || variants.some(v => !v.size)) return;
    const panel = doc.querySelector('[data-aw-tire-offer]');
    const root = panel && panel.closest('.product--outer');
    const selection = root && root.querySelector('variant-selection');
    const form = selection && selection.closest('form[data-product-form]');
    const nativeOptions = selection && selection.querySelector('options-selection');
    const master = selection && selection.querySelector('[name="id"]');
    if (!form || !nativeOptions || !master || root.querySelector('[data-aw-size-selector]')) return;
    await win.customElements.whenDefined('variant-selection');
    // Do not replace a working native selector when its own product source is unavailable.
    const nativeProduct = await selection.getProduct();
    if (!nativeProduct || !nativeProduct.variants) return;

    const create = (tag, text, className) => {
      const node = doc.createElement(tag);
      if (text) node.textContent = text;
      if (className) node.className = className;
      return node;
    };
    const box = create('fieldset', '', 'aw-tire-size-selector');
    box.setAttribute('data-aw-size-selector', '');
    box.append(create('legend', 'Choose tire size'));
    const grid = create('div', '', 'aw-tire-size-selector__fields');
    const controls = {};
    const labels = { width: 'Width (mm)', profile: 'Profile (%)', diameter: 'Wheel (in)', id: 'Exact tire specification' };
    [...fields, 'id'].forEach(field => {
      const label = create('label', labels[field]);
      const select = create('select');
      select.setAttribute('aria-label', labels[field]);
      select.dataset.sizeField = field;
      label.append(select); controls[field] = select;
      if (field === 'id') { label.className = 'aw-tire-size-selector__spec'; box.append(label); }
      else grid.append(label);
    });
    box.insertBefore(grid, controls.id.parentElement);
    const status = create('p', '', 'aw-tire-size-selector__status');
    status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    box.append(status);
    selection.before(box);

    const alternatives = create('section', '', 'aw-tire-alternatives');
    alternatives.hidden = true;
    root.after(alternatives);
    let catalogPromise;
    const catalog = () => {
      if (!catalogPromise) {
        const url = new URL(data.catalogUrl, win.location.origin);
        const preview = new URL(win.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const controller = new win.AbortController();
        const timer = win.setTimeout(() => controller.abort(), 4000);
        catalogPromise = win.fetch(url.href, { credentials: 'same-origin', signal: controller.signal })
          .then(response => { if (!response.ok) throw new Error('Catalog unavailable'); return response.text(); })
          .then(html => JSON.parse(new win.DOMParser().parseFromString(html, 'text/html').querySelector('[data-aw-tire-size-index]').textContent))
          .catch(() => null).finally(() => win.clearTimeout(timer));
      }
      return catalogPromise;
    };
    let revision = 0;
    const renderAlternatives = async (variant, version) => {
      alternatives.hidden = true; alternatives.replaceChildren();
      if (!variant) return;
      const items = alternativesFor(await catalog(), data.productId, variant);
      if (version !== revision || !items.length) return;
      alternatives.append(create('h2', 'Other tires in ' + variant.size.width + '/' + variant.size.profile + 'R' + variant.size.diameter));
      alternatives.append(create('p', 'Same dimensions do not confirm fitment. Compare tire type and required load/speed ratings with your vehicle’s placard and manual.'));
      const list = create('ul', '', 'aw-tire-alternatives__list');
      items.forEach(item => {
        const li = create('li');
        const link = create('a', item.model); link.href = item.url;
        li.append(link, create('p', item.title, 'aw-tire-alternatives__code'), create('p', item.priceText + ' / tire'));
        li.append(create('p', item.difference || 'Same tire type and ratings', 'aw-tire-alternatives__difference'));
        li.append(create('span', 'Available · View product details', 'aw-tire-alternatives__availability'));
        list.append(li);
      });
      alternatives.append(list); alternatives.hidden = false;
    };
    let currentState = {};
    let navigation = { url: win.location.href, state: win.history.state };
    const historyState = state => ({ ...(win.history.state || {}), awTireSize: { productId: data.productId, ...state } });
    const fill = (control, options, value, placeholder) => {
      const first = create('option', placeholder); first.value = '';
      control.replaceChildren(first);
      options.forEach(option => {
        const node = create('option', option.label || option.value + (option.available ? '' : ' — unavailable'));
        node.value = option.value; control.append(node);
      });
      control.value = value || '';
    };
    const render = result => {
      currentState = result.state;
      fields.forEach(field => {
        const options = optionsFor(variants, currentState, field);
        fill(controls[field], options, currentState[field], 'Choose');
        controls[field].disabled = !options.length;
      });
      controls.id.parentElement.hidden = result.matches.length < 2;
      fill(controls.id, result.matches.map(v => ({ value: String(v.id), label: v.title + (v.available ? '' : ' — unavailable') })), currentState.id, 'Choose specification');
      const missing = fields.find(field => !currentState[field]);
      status.textContent = result.variant ? 'Selected: ' + result.variant.title + (result.variant.available ? '' : ' · Unavailable') :
        missing ? 'Choose ' + ({ width: 'width', profile: 'profile', diameter: 'wheel diameter' })[missing] + ' to continue.' : 'Choose the exact tire specification to continue.';
      renderAlternatives(result.variant, ++revision);
    };
    const commit = (result, mode) => {
      render(result);
      const id = result.variant ? String(result.variant.id) : 'not-selected';
      const url = new URL(mode === 'restore' ? win.location.href : navigation.url);
      url.searchParams.set('variant', id);
      navigation = { url: url.href, state: historyState(currentState) };
      win.history[mode === 'push' ? 'pushState' : 'replaceState'](navigation.state, '', navigation.url);
      master.value = id;
      selection.variant = id;
      // The native master-change listener drives the same variant event as the old pills.
      master.dispatchEvent(new win.Event('change'));
    };
    box.addEventListener('change', event => {
      const field = event.target.dataset.sizeField;
      if (!field) return;
      // A synthetic size change must not reset the shopper's manually chosen quantity.
      event.stopPropagation();
      commit(reconcile(variants, { ...currentState, [field]: event.target.value }), 'push');
    });
    selection.addEventListener('variant-change', event => {
      const eventId = event.detail.variant ? String(event.detail.variant.id) : 'not-selected';
      const selectedId = selection.getAttribute('variant');
      const activeId = variants.some(v => String(v.id) === selectedId) ? selectedId : 'not-selected';
      const restoreNativeState = () => {
        const version = revision;
        win.setTimeout(() => {
          if (version !== revision) return;
          master.value = currentState.id || 'not-selected';
          win.history.replaceState(navigation.state, '', navigation.url);
        }, 0);
      };
      if (eventId !== activeId) {
        event.stopImmediatePropagation();
        // Empire writes its master again after dispatch resolves, even for a stale event.
        restoreNativeState();
        return;
      }
      if (eventId !== (currentState.id || 'not-selected')) {
        const variant = variants.find(v => String(v.id) === eventId);
        render(reconcile(variants, variant ? { ...variant.size, id: eventId } : {}, false));
        const url = new URL(navigation.url); url.searchParams.set('variant', eventId);
        navigation = { url: url.href, state: historyState(currentState) };
      }
      // Empire replaces the URL during its event. Restore attribution/history after native work.
      restoreNativeState();
    }, true);
    const fromURL = () => {
      const url = new URL(win.location.href);
      const requestedId = url.searchParams.has('variant') ? url.searchParams.get('variant') : data.initial;
      const variant = variants.find(v => String(v.id) === String(requestedId));
      if (variant) return reconcile(variants, { ...variant.size, id: String(variant.id) }, false);
      const saved = win.history.state && win.history.state.awTireSize;
      return reconcile(variants, requestedId === 'not-selected' && saved && String(saved.productId) === String(data.productId) ? saved : {}, false);
    };
    win.addEventListener('popstate', () => commit(fromURL(), 'restore'));
    commit(fromURL(), 'restore');
    nativeOptions.setAttribute('data-aw-size-native-hidden', '');
    box.dataset.ready = 'true';
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { parse, geometryKey, optionsFor, reconcile, differentSpec, alternativesFor, init };
  if (typeof document !== 'undefined') {
    const start = () => init(window, document).catch(() => { /* Keep native selection when initialization cannot complete. */ });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  }
})();
